import { useEffect, useRef } from "react";
import type { Route } from "./+types/brands";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { detectLocale, translations } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta } from "~/lib/seo";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  return { locale };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = data?.locale ?? "en";
  const t = translations[locale].forBrands.meta;
  return [
    { title: t.title },
    { name: "description", content: t.description },
    ...socialMeta({ title: t.title, description: t.description, pathname: "/brands", locale }),
  ];
}

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add("visible"); },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

const ROW_A = ["FD001-251225032","FD001-251225033","FD001-251225034","FD002-251014004","FD002-260210001","FD012-251211019","FD016-251224017","FD016-251224019","FD028-251224009","FD028-251224010","FD028-251224011"];
const ROW_B = ["FD029-251231031","FD030-251223016","FD033-251225049","FD033-251225050","FD036-251231032","FD037-251225025","FD040-251231021","FD051-251226013","FD064-260105031","FD065-260105024","FD062-260105005"];

export default function ForBrandsPage() {
  const { t } = useT();
  const h = t.forBrands;

  const teaserRef = useReveal(0.1);
  const ctaRef = useReveal();

  return (
    <MarketingLayout>

      {/* ══ HERO ══ */}
      <section className="px-6 lg:px-12 pt-16 pb-14 lg:pt-24 lg:pb-20 relative">
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={i} x1={`${(i + 1) * 9}%`} y1="0" x2={`${(i + 1) * 9}%`} y2="100%" stroke="oklch(0.085 0.004 50)" strokeWidth="1" opacity="0.02" />
          ))}
        </svg>

        <p className="issue-label mb-6 relative" style={{ fontFamily: "var(--font-sans)" }}>{h.hero.overline}</p>

        <h1 className="font-display font-light leading-[0.92] tracking-tight text-foreground max-w-4xl relative" style={{ fontSize: "clamp(3.5rem, 8vw, 7.5rem)" }}>
          {h.hero.h1Line1}
          <br />
          {h.hero.h1Line2}
          <br />
          <em className="not-italic" style={{ fontWeight: 300, opacity: 0.42 }}>{h.hero.h1Line3}</em>
        </h1>

        <div className="mt-12 grid lg:grid-cols-[1fr_480px] items-start gap-10 relative">
          <div className="space-y-8 lg:pt-2">
            <p className="font-display font-light text-muted-foreground" style={{ fontSize: "clamp(1.05rem, 1.8vw, 1.4rem)", lineHeight: 1.5, maxWidth: "44ch" }}>{h.hero.tagline}</p>
            <div className="flex flex-wrap gap-4 items-center">
              <a href="/sign-in?redirect=/trade/catalog" className="inline-block px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>{h.hero.ctaPrimary}</a>
              <a href="/brands/how-it-works" className="inline-block px-6 py-4 border border-foreground/20 hover:border-foreground/50 transition-colors text-foreground" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>{h.hero.ctaSecondary}</a>
            </div>
          </div>
          <div className="hidden lg:block relative overflow-hidden bg-muted" style={{ aspectRatio: "4/3" }}>
            <img src="https://images.unsplash.com/photo-1596944924616-7b38e7cfac36?auto=format&fit=crop&w=480&q=80" alt="" aria-hidden="true" className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.02]" style={{ filter: "grayscale(0.10) contrast(1.05) saturate(0.85)" }} loading="eager" />
          </div>
        </div>
      </section>

      {/* ── Stat bar ── */}
      <div className="px-6 lg:px-12">
        <div className="rule" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
          {([
            ["3", h.stats.tiers],
            [h.stats.designerDirect, h.stats.noIntermediaries],
            [h.stats.contentReady, h.stats.contentReadySub],
            [h.stats.productionManaged, h.stats.endToEnd],
          ] as [string, string][]).map(([value, label], i) => (
            <div key={label} className={`py-5 ${i > 0 ? "pl-6 border-l border-border" : ""}`}>
              <p className="font-display font-light text-foreground leading-none mb-1" style={{ fontSize: "clamp(0.9rem, 1.6vw, 1.3rem)" }}>{value}</p>
              <p className="issue-label" style={{ fontFamily: "var(--font-sans)", opacity: 0.45 }}>{label}</p>
            </div>
          ))}
        </div>
        <div className="rule" />
      </div>

      {/* ── Marquee ── */}
      <section className="py-6 space-y-3" aria-label={h.marquee.ariaLabel}>
        <div className="overflow-hidden w-full">
          <div className="flex gap-3 w-max" style={{ animation: "marquee-left 55s linear infinite" }}>
            {[...ROW_A,...ROW_A].map((sku,i) => <div key={`${sku}-${i}`} className="shrink-0 overflow-hidden bg-muted" style={{ width:"160px",aspectRatio:"2/3" }}><img src={`/designs/${sku}/001.jpg`} alt="" aria-hidden="true" className="w-full h-full object-cover" loading="lazy" /></div>)}
          </div>
        </div>
        <div className="overflow-hidden w-full">
          <div className="flex gap-3 w-max" style={{ animation: "marquee-right 65s linear infinite" }}>
            {[...ROW_B,...ROW_B].map((sku,i) => <div key={`${sku}-${i}`} className="shrink-0 overflow-hidden bg-muted" style={{ width:"160px",aspectRatio:"2/3" }}><img src={`/designs/${sku}/001.jpg`} alt="" aria-hidden="true" className="w-full h-full object-cover" loading="lazy" /></div>)}
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ HOW IT WORKS — TEASER ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-20">
        <div ref={teaserRef} className="reveal">
          <div className="flex items-baseline justify-between mb-10">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.process.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.process.subtitle}</span>
          </div>

          <div className="grid lg:grid-cols-3 gap-0">
            {h.process.teaserItems.map((s, i) => (
              <div key={i} className="relative">
                {i > 0 ? <div className="hidden lg:block absolute inset-y-0 left-0 w-px bg-border" /> : null}
                <div className="py-8" style={{ paddingLeft: i > 0 ? "var(--col-pad)" : undefined, paddingRight: i < h.process.teaserItems.length - 1 ? "var(--col-pad)" : undefined }}>
                  <div className="rule mb-5" />
                  <span className="font-display font-light select-none block mb-4" style={{ fontSize: "clamp(2rem, 3vw, 3rem)", lineHeight: 1, color: "var(--primary)", opacity: 0.45 }}>{["I","II","III"][i]}</span>
                  <h3 className="font-display font-normal text-foreground leading-tight mb-3" style={{ fontSize: "clamp(1.1rem, 1.6vw, 1.35rem)" }}>{s.heading}</h3>
                  <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.8rem", maxWidth: "32ch" }}>{s.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <a href="/brands/how-it-works" className="inline-block px-8 py-4 border border-foreground/20 hover:border-foreground/50 transition-colors text-foreground" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>{h.process.teaserCta}</a>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ CARA AI — ENGINEERING INTELLIGENCE (teaser → /ai) ══ */}
      <section id="cara-ai" className="px-6 lg:px-12 py-20 lg:py-28 relative overflow-hidden">
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          {Array.from({ length: 12 }).map((_, i) => (
            <line key={i} x1={`${(i + 1) * 8}%`} y1="0" x2={`${(i + 1) * 8}%`} y2="100%" stroke="oklch(0.085 0.004 50)" strokeWidth="1" opacity="0.025" />
          ))}
        </svg>

        <div className="relative max-w-6xl mx-auto">
          <span className="issue-label block mb-5" style={{ fontFamily: "var(--font-sans)", color: "var(--teal-sapphire)" }}>
            {h.caraAI.overline}
          </span>

          <h2
            className="font-display font-light leading-[0.95] tracking-tight text-foreground mb-6"
            style={{ fontSize: "clamp(2.2rem, 5vw, 4rem)" }}
          >
            {h.caraAI.heading.split("\n").map((line: string, i: number) => (
              <span key={i}>
                {i === 0 ? line : <em className="not-italic" style={{ opacity: 0.42 }}>{line}</em>}
                {i === 0 && <br />}
              </span>
            ))}
          </h2>

          <p
            className="text-muted-foreground leading-relaxed mb-10"
            style={{ fontSize: "0.95rem", maxWidth: "62ch" }}
          >
            {h.caraAI.intro}
          </p>

          {/* ── Stat strip (pillars condensed) ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border border-border mb-10">
            {h.caraAI.pillars.map((pillar: { stat: string; label: string; body: string }, i: number) => (
              <div key={pillar.label} className="relative">
                {i > 0 && <div className="hidden sm:block absolute inset-y-0 left-0 w-px bg-border" />}
                <div className="p-6 lg:p-8 text-center">
                  <span
                    className="font-display font-light block mb-1"
                    style={{ fontSize: "clamp(1.6rem, 2.5vw, 2.2rem)", lineHeight: 1, color: "var(--teal-sapphire)" }}
                  >
                    {pillar.stat}
                  </span>
                  <span
                    className="issue-label block"
                    style={{ fontFamily: "var(--font-sans)", opacity: 0.5 }}
                  >
                    {pillar.label}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-4 items-center">
            <a
              href="/cara/demo?for=brands"
              className="inline-block px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors"
              style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}
            >
              {h.caraAI.tryCara}
            </a>
            <a
              href="/brands/ai"
              className="inline-block px-6 py-4 border border-foreground/20 hover:border-foreground/50 transition-colors text-foreground"
              style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}
            >
              {h.caraAI.learnMore}
            </a>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ CTA ══ */}
      <section className="px-6 lg:px-12 py-20 lg:py-28">
        <div ref={ctaRef} className="reveal grid lg:grid-cols-[1fr_auto] items-end gap-8">
          <div>
            <span className="issue-label block mb-5" style={{ fontFamily: "var(--font-sans)" }}>{h.cta.overline}</span>
            <h2 className="font-display font-light leading-[0.92] tracking-tight text-foreground" style={{ fontSize: "clamp(2.8rem, 6vw, 5.5rem)" }}>
              {h.cta.heading.split("\n").map((line, i) => (
                <span key={i}>{i === 0 ? line : <em className="not-italic" style={{ fontWeight: 300, opacity: 0.45 }}>{line}</em>}{i === 0 && <br />}</span>
              ))}
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed" style={{ fontSize: "0.9rem", maxWidth: "44ch" }}>{h.cta.body}</p>
          </div>
          <div className="pb-2 space-y-4">
            <a href="/sign-in?redirect=/trade/catalog" className="block px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors text-center" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>{h.cta.primary}</a>
            <a href="/brands/how-it-works" className="block px-8 py-3 border border-foreground/20 hover:border-foreground/40 transition-colors text-center text-foreground" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>{h.cta.secondary}</a>
            <p className="text-center text-muted-foreground" style={{ fontSize: "0.7rem" }}>{h.cta.tradeOnly}</p>
          </div>
        </div>
      </section>

    </MarketingLayout>
  );
}
